import { useState } from "react";
import { ImageOff } from "lucide-react";

interface Props {
  orgSlug: string;
  projectSlug: string;
  deploymentId: string;
  screenshotAt: string | null;
  /** Used for the img alt + the title attribute. */
  label?: string;
}

/**
 * v4 (004-project-overview-tab) — thumbnail image with placeholder fallback.
 *
 * If `screenshotAt` is null, we render the placeholder immediately (no
 * network request for an image we know isn't there). If the request 404s
 * after navigation (e.g. capture was deleted out from under us), we also
 * fall back to the placeholder. The PNG endpoint sets a 1h Cache-Control
 * so repeat visits don't re-fetch.
 */
export function DeploymentScreenshot({
  orgSlug,
  projectSlug,
  deploymentId,
  screenshotAt,
  label,
}: Props) {
  const [errored, setErrored] = useState(false);

  if (!screenshotAt || errored) return <Placeholder />;

  const src = `/api/v1/orgs/${orgSlug}/projects/${projectSlug}/deployments/${deploymentId}/screenshot.png`;
  return (
    <img
      src={src}
      alt={label ?? "Deployment screenshot"}
      title={label}
      loading="lazy"
      onError={() => setErrored(true)}
      className="w-full h-full object-cover object-top"
    />
  );
}

function Placeholder() {
  return (
    <div className="w-full h-full bg-subtle flex flex-col items-center justify-center gap-2 text-2xs text-subtle-foreground">
      <ImageOff className="h-5 w-5" />
      <span>screenshot pending</span>
    </div>
  );
}
